import axios, { AxiosError } from 'axios';
import serviceBrowserStorage, {
  BrowserStorage,
  IStorageObject,
} from '../../browserstorage/BrowserStorage';
import { Auth, IAuthResponse, ISessionObject, ITokenObject } from '../Auth';
import { Logger, LogSender } from '../../logger';
export const AUTH_NAMESPACE = 'auth';
export const STORAGE_NAMESPACE_TOKEN = 'token';
export const STORAGE_NAMESPACE_SESSION = 'session';
export type SessionExpiredListener = (e: boolean) => void;
export type TokenRefreshListener = (token: ITokenObject) => void;
export abstract class AuthProvider<T> {
  protected storage: IStorageObject = null;

  protected userTimeOffset = 0;
  protected sessionLatency = 0;
  protected tokenLatency = 0;

  protected auth: Auth;
  protected tokenPromise: Promise<ITokenObject> = null;
  public sessionExpiredListeners: SessionExpiredListener[] = [];
  public tokenRefreshListeners: TokenRefreshListener[] = [];

  private serviceBrowserStorage: BrowserStorage;
  /**
   * @hidden
   */
  public get BrowserStorage(): BrowserStorage {
    return this.serviceBrowserStorage;
  }

  constructor(auth: Auth) {
    this.auth = auth;
    this.initSessionExpired();
    this.serviceBrowserStorage = serviceBrowserStorage;
    this.storage = this.BrowserStorage.getStorageObject(AUTH_NAMESPACE)
      ? this.BrowserStorage.getStorageObject(AUTH_NAMESPACE)
      : this.BrowserStorage.createStorageObject(AUTH_NAMESPACE, 'localStorage');
    this.storage.get(STORAGE_NAMESPACE_SESSION).then((sobj) => {
      if (sobj) {
        this.userTimeOffset = sobj.userTimeOffset;
        this.sessionLatency = sobj.sessionLatency;
        this.tokenLatency = sobj.tokenLatency;
      }
    });
  }

  public abstract login(credential: T): Promise<boolean | ITokenObject>;
  /**
   * Get session details from localstorage
   *
   * ```js example
   * dory.Auth.getSession()
   *   .catch(function (error) {
   *     console.error(error); // catch api errors
   *   })
   *   .then(function (result) {
   *     console.log(result);
   *     // > { exp, msLeft, sid, sid_core, sid_ngp, user }
   *   });
   * ```
   */
  public getSession(): Promise<ISessionObject> {
    if (this.auth.CookieSession) {
      return Promise.resolve<ISessionObject>({
        exp: Date.now() + 60000,
        initialDuration: 60000,
        isSSO: false,
        msLeft: 60000,
        sid: '', // cookie
        sid_core: null,
        sid_ngp: '', // cookie
        user: '',
      });
    }
    return this.storage.get(STORAGE_NAMESPACE_SESSION).then((sObj) => {
      if (sObj) {
        sObj.exp *= 1000; // convert to ms
        sObj.msLeft = this.getTimeToExpiry(sObj.exp, this.sessionLatency);
      }
      return Promise.resolve(sObj as ISessionObject);
    });
  }

  public isSessionValid(): Promise<boolean> {
    return this.getSession().then((so) => {
      return Promise.resolve(!!(so && Date.now() < so.exp));
    });
  }

  protected createSession(
    loginResponse: IAuthResponse,
    user: string,
    latency: number,
    isSSO = false
  ): Promise<ISessionObject> {
    if (this.validateSessionResponse(loginResponse)) {
      this.userTimeOffset = this.getUserTimeOffset(loginResponse.data.token);
      this.tokenLatency = latency;
      this.sessionLatency = latency;

      return this.setSessionObject({
        user,
        isSSO,
        sid: loginResponse.data.sid,
        exp: loginResponse.data.expiresAt,
        initialDuration: loginResponse.data.expiresAt * 1000 - Date.now(),
        sid_core: null, // TODO
        sid_ngp: null, // TODO
        msLeft: null, // reset here
        userTimeOffset: this.userTimeOffset,
        tokenLatency: latency,
        sessionLatency: latency,
        token: loginResponse.data.token,
      }).then(() => this.getSession());
    }

    return this.setSessionObject(null).then(() =>
      Promise.reject('MISSING_SESSIONID_IN_RESPONSE')
    );
  }

  // Update the stored session expiry concerning the current time.
  protected async updateSessionExpiry(token: string): Promise<boolean> {
    if (this.auth.CookieSession) {
      return;
    }
    return this.storage.get(STORAGE_NAMESPACE_SESSION).then((sobj) => {
      if (sobj) {
        // Update the stored token latency and time offset after token refresh.
        sobj.tokenLatency = this.tokenLatency;
        sobj.userTimeOffset = this.getUserTimeOffset(token);
        // @deprecated this is a workaround to mitigate the previous error. When all stored
        // sessions have a initialDuration set, this can be removed
        const duration = sobj.initialDuration
          ? sobj.initialDuration
          : 3 * 24 * 60 * 60 * 1000;
        // has to be converted to seconds from ms to match the same time unit the middleware provides
        // in requestSession
        sobj.exp = Math.floor((duration + Date.now()) / 1000);
        return this.setSessionObject(sobj);
      }
    });
  }

  /**
   * @hidden
   */
  public validateSessionResponse(response: IAuthResponse): boolean {
    return response.data?.sid !== undefined;
  }

  public async setSessionObject(sobj: ISessionObject): Promise<boolean> {
    return this.storage
      .set(STORAGE_NAMESPACE_SESSION, sobj)
      .then(() => {
        Logger.verbose(
          LogSender.AUTH,
          `Session updated:\n${JSON.stringify(sobj, null, 2)}`
        );
        return true;
      })
      .catch((e) => {
        return e;
      });
  }

  //#region Session/Token Handling
  /**
   * Get the encoded token-string from localStorage
   */
  public getTokenString(): Promise<string> {
    return this.storage.get(STORAGE_NAMESPACE_TOKEN);
  }

  /**
   * Get the decoded token from localstorage
   *
   * ```js example
   * dory.Auth.getToken()
   *   .catch(function (error) {
   *     console.error(error); // catch api errors
   *   })
   *   .then(function (result) {
   *     console.log(result);
   *     // > { did, ety, exp, grp, iat, iss, jti, msLeft, nbf }
   *   });
   * ```
   */
  public getToken(): Promise<ITokenObject> {
    return this.getTokenString().then((value) => {
      const tkn = this.decodeToken(value);
      if (tkn) {
        tkn.exp *= 1000; // convert to ms
        tkn.iat *= 1000; // convert to ms
        tkn.msLeft = this.getTimeToExpiry(tkn.exp, this.tokenLatency);
      }
      return tkn;
    });
  }

  /**
   * This function requests a new token with the session stored in localStorage
   *
   * ```js example
   * dory.Auth.refreshToken()
   *  .catch(function (error) {
   *    console.error(error); // catch api errors
   *  })
   *  .then(function (result) {
   *    console.log(result);
   *  });
   * ```
   */
  public async refreshToken(): Promise<boolean> {
    return this.isSessionValid()
      .then((isValid) => {
        if (!isValid) {
          return this.emitSessionExpired().then(() =>
            Promise.reject('Session is invalid')
          );
        } else {
          return this.getSession();
        }
      })
      .then((sObj) => {
        return this.requestToken(sObj)
          .then(() => true)
          .catch((error: AxiosError) => {
            return Promise.reject(`Failed to refresh token: ${error}`);
          });
      });
  }

  /**
   * Get Token from credentials
   */
  protected getTokenFromCredentials(
    sessionObject: ISessionObject
  ): Promise<boolean> {
    if (sessionObject.token) {
      this.setToken(sessionObject.token);
      return Promise.resolve(true);
    }

    return this.refreshToken();
  }

  public setToken(token: string): Promise<IStorageObject> {
    return this.storage
      .set(STORAGE_NAMESPACE_TOKEN, token)
      .then(() => this.getToken())
      .then((storageToken) => {
        if (!storageToken) {
          return null;
        }

        this.userTimeOffset =
          token !== null ? storageToken.iat - Date.now() : 0;
      });
  }

  private decodeToken(token: string) {
    if (!token || typeof token !== 'string') {
      return null;
    }

    return JSON.parse(atob(token.split('.')[1])) as ITokenObject;
  }

  private getUserTimeOffset(token: string) {
    return this.decodeToken(token).iat * 1000 - Date.now();
  }
  private getTimeToExpiry(exp: number, latency: number) {
    return exp - new Date().getTime() + this.userTimeOffset - latency;
  }
  public emitTokenRefresh(token: ITokenObject): void {
    for (const listener of this.tokenRefreshListeners) {
      try {
        listener(token);
      } catch (e) {
        Logger.error(LogSender.STREAMING, `call to emitTokenRefresh failed`, e);
      }
    }
  }
  //#endregion

  protected logoutFinished(): Promise<boolean> {
    return this.setToken(null).then(() => {
      return this.setSessionObject(null);
    });
  }
  protected validateTokenResponse(response: IAuthResponse): boolean {
    return response.data !== null;
  }

  protected validateLogoutResponse(response: IAuthResponse): boolean {
    return response.data !== null; // TODO
  }

  protected validateConfig() {
    const authConfig = this.auth.Config.auth;
    // check if config is set before login starts.
    if (!authConfig.url || !authConfig.urlToken || !authConfig.urlSession) {
      throw new Error(
        'MISSING BASEPATH CONFIG. Use config.setBasePath() to set the base path!'
      );
    }
  }

  public logout(): Promise<boolean> {
    return this.getSession().then((sObj) => {
      const sid = sObj !== null ? sObj.sid : null;

      if (!sid) {
        this.logoutFinished();
        return Promise.resolve(true);
      }

      return this.auth
        .request({
          url: this.auth.Config.auth.urlSession,
          method: 'delete',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          data: {
            sid: sObj?.sid,
          },
        })
        .then((response) => {
          this.logoutFinished();
          return this.validateLogoutResponse(response);
        })
        .catch(() => {
          this.logoutFinished();
          return Promise.resolve(false);
        });
    });
  }

  /**
   * Use this function to emit the session expired event to all listeners
   */
  public emitSessionExpired(): Promise<void> {
    return this.getSession()
      .then((session) => {
        if (session) {
          return this.setSessionObject(null)
            .then(() => this.setToken(null))
            .then(() => true);
        } else {
          return false;
        }
      })
      .then((notify) => {
        if (notify) {
          for (const listener of this.sessionExpiredListeners) {
            try {
              listener(true);
            } catch (e) {
              Logger.error(
                LogSender.STREAMING,
                `call to sessionExpiredListener failed`,
                e
              );
            }
          }
        }
      });
  }

  public requestToken(sessionObj?: ISessionObject): Promise<ITokenObject> {
    if (this.tokenPromise !== null) {
      // Return the Promise for the already-requested token to avoid multiple token requests
      return this.tokenPromise;
    }

    const header = { sid: sessionObj.sid };
    // const header = this.apiKey ? { 'X-API-KEY': this.apiKey } : { sid: sessionObj.sid };
    const startTime = Date.now();

    this.tokenPromise = this.auth
      .request({
        url: this.auth.Config.auth.urlToken,
        method: 'post',
        headers: header,
      })
      .then(async (response: IAuthResponse) => {
        if (!this.validateTokenResponse(response)) {
          return this.setToken(null).then(() => {
            throw new Error('MISSING_TOKEN_IN RESPONSE');
          });
        }
        this.tokenLatency = Date.now() - startTime;
        this.updateSessionExpiry(response.data);
        return this.setToken(response.data)
          .then(() => this.getToken())
          .then((token) => {
            setTimeout(() => this.emitTokenRefresh(token)); //emit the token refresh listeners
            return token;
          });
      })
      .catch((error: AxiosError) => {
        return Promise.reject(error);
      })
      .finally(() => {
        this.tokenPromise = null;
      });
    return this.tokenPromise;
  }

  /**
   * Initialize the required watchers to detect an expired session
   */
  protected initSessionExpired(): void {
    axios.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        const retryUrlFragment = '__dory-token-retry';
        const isRetry = error.config.url.indexOf(`${retryUrlFragment}=true`);
        if (
          isRetry === -1 &&
          error.response?.status === 401 &&
          error.request?.responseURL !== this.auth.getSessionEndpoint() &&
          error.request?.responseURL !== this.auth.getTokenEndpoint()
        ) {
          return this.refreshToken()
            .then((response) => {
              if (response) {
                // remove old timestamp form url
                const url = new URL(error.config.url);
                url.searchParams.append(retryUrlFragment, 'true');
                return this.auth
                  .getTokenString()
                  .then((jwt) => {
                    const headers = error.config.headers;
                    if (jwt) {
                      headers.jwt = jwt;
                    }
                    return this.auth.request({
                      url: url.toString(),
                      method: error.config.method,
                      data: error.config.data,
                      headers,
                      timeout: error.config.timeout,
                    });
                  })
                  .catch(() => {
                    this.emitSessionExpired();
                  });
              } else {
                this.emitSessionExpired();
                throw new Error("Request failed and couldn't refresh token");
              }
            })
            .catch((error) => {
              if (error.response?.status === 401) {
                this.emitSessionExpired();
                return Promise.reject(error);
              }
            });
        } else if (
          error.response?.status === 401 &&
          error.request?.responseURL === this.auth.getTokenEndpoint()
        ) {
          // Handle cases where token call by itself returns 401
          this.emitSessionExpired();
          return Promise.reject(error);
        } else {
          return Promise.reject(error);
        }
      }
    );
  }
}
